import { ChangeEvent, FunctionComponent, ReactNode, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { faLifeRing, faPaperclip, faXmark } from "@fortawesome/pro-regular-svg-icons";

import {
	CreateTicketRequest,
	CUSTOMER_TICKET_CATEGORIES,
	TICKET_ATTACHMENT_ALLOWED_MIME_TYPES,
	TICKET_ATTACHMENT_MAX_FILES,
	TICKET_MESSAGE_MAX_LENGTH,
	TICKET_SUBJECT_MAX_LENGTH,
	TicketCategory,
	TicketLinkedEntity,
} from "@signalist/shared-types";

import { Icon } from "@/components/Icon/Icon";
import { InputField } from "@/components/InputField/InputField";
import { Modal } from "@/components/Modals/Modal";
import { classNames } from "@/utils/classNames";

import { useHelpContext } from "@/components/HelpCenter/helpContext";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import {
	useCreateTicketMutation,
	useUploadTicketAttachmentMutation,
} from "@/pages/Private/redux/tickets/tickets.api";
import { useAppSelector } from "@/redux/hooks";

import {
	AttachmentRejection,
	formatFileSize,
	selectTicketAttachments,
	TICKET_ATTACHMENT_MAX_MB,
} from "./ticketAttachments";

/** What a caller (a row menu, the Support page) can put into the form before it opens. */
export interface NewTicketPrefill {
	subject?: string;
	message?: string;
	category?: TicketCategory;
	linkedEntity?: TicketLinkedEntity;
	/** Shown in the read-only "Related to" chip; falls back to "<Type> #<id>". */
	linkedEntityLabel?: string;
}

/**
 * How a host other than the customer form submits (PREC-3939 decision 7): the staff modal files
 * through `tickets/admin` and uploads through the staff route. Everything else — the fields, the
 * attachment policy, the create-then-upload order — stays this component's.
 */
export interface NewTicketOverrides {
	/** Replaces `POST tickets`; resolves with the new ticket's id. */
	createTicket: (body: CreateTicketRequest) => Promise<{ id: number }>;
	/** Replaces `POST tickets/:id/attachments`, one call per file. */
	uploadAttachment: (args: { ticketId: number; file: File }) => Promise<unknown>;
	/** Replaces the "Filed as {{name}} · {{company}}" footer line. */
	filedAs: string;
	title?: string;
	intro?: string;
	/** Rendered between the intro and the category chips (the staff pickers). */
	leading?: ReactNode;
	/** The category chips to offer; defaults to the customer's, which exclude the staff-only ones. */
	categories?: readonly TicketCategory[];
	/** Runs before anything is sent; `false` aborts and the host shows its own error. */
	canSubmit?: () => boolean;
	/** The host's own mutations in flight — disables the submit like the built-in ones do. */
	isBusy?: boolean;
}

interface NewTicketModalProps {
	prefill?: NewTicketPrefill;
	onClose: () => void;
	/** Called once the ticket exists; `failedAttachment` names a file that could not be uploaded. */
	onCreated?: (ticketId: number, failedAttachment?: string) => void;
	overrides?: NewTicketOverrides;
}

const FormSchema = z.object({
	subject: z.string().trim().min(1).max(TICKET_SUBJECT_MAX_LENGTH),
	message: z.string().trim().min(1).max(TICKET_MESSAGE_MAX_LENGTH),
});

type FormValues = z.infer<typeof FormSchema>;

/**
 * File a support ticket (PREC-3937). The same form serves the Support page's "New ticket" button
 * and every "Report a problem" menu entry, which only differ by what they prefill.
 *
 * Attachments cannot travel with the create call — the API needs the ticket id first — so they are
 * uploaded one request at a time afterwards. A failed upload never discards the ticket the
 * customer just wrote; the caller reports which file did not make it.
 */
export const NewTicketModal: FunctionComponent<NewTicketModalProps> = ({
	prefill,
	onClose,
	onCreated,
	overrides,
}) => {
	const { t } = useTranslation();
	const { user } = useAppSelector(authSelector);
	const { routePattern, label } = useHelpContext();

	const categories = overrides?.categories ?? CUSTOMER_TICKET_CATEGORIES;
	const [category, setCategory] = useState<TicketCategory>(prefill?.category ?? TicketCategory.BUG);
	const [files, setFiles] = useState<File[]>([]);
	const [rejection, setRejection] = useState<AttachmentRejection | null>(null);
	const [hasError, setHasError] = useState(false);

	const [createTicket, { isLoading: isCreating }] = useCreateTicketMutation();
	const [uploadAttachment, { isLoading: isUploading }] = useUploadTicketAttachmentMutation();

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<FormValues>({
		resolver: zodResolver(FormSchema),
		defaultValues: { subject: prefill?.subject ?? "", message: prefill?.message ?? "" },
	});

	const handlePick = (event: ChangeEvent<HTMLInputElement>) => {
		const selection = selectTicketAttachments(files, Array.from(event.target.files ?? []));

		setFiles(selection.files);
		setRejection(selection.rejection);
		// Let the same file be picked again after it was removed.
		event.target.value = "";
	};

	const handleRemove = (name: string) => {
		setFiles(files.filter((file) => file.name !== name));
		setRejection(null);
	};

	const create = overrides
		? overrides.createTicket
		: (body: CreateTicketRequest) => createTicket(body).unwrap();
	const upload = overrides
		? overrides.uploadAttachment
		: (args: { ticketId: number; file: File }) => uploadAttachment(args).unwrap();

	const onSubmit = async (values: FormValues) => {
		if (overrides?.canSubmit && !overrides.canSubmit()) {
			return;
		}

		setHasError(false);

		let ticketId: number;

		try {
			const ticket = await create({
				subject: values.subject,
				category,
				message: values.message,
				pageContext: { routePattern, label },
				...(prefill?.linkedEntity ? { linkedEntity: prefill.linkedEntity } : {}),
			});

			ticketId = ticket.id;
		} catch {
			setHasError(true);

			return;
		}

		// The ticket exists from here on: an upload that fails costs a file, never the ticket.
		let failedAttachment: string | undefined;

		for (const file of files) {
			try {
				await upload({ ticketId, file });
			} catch {
				failedAttachment = file.name;
				break;
			}
		}

		onCreated?.(ticketId, failedAttachment);
		onClose();
	};

	const linkedEntityText =
		prefill?.linkedEntityLabel ??
		(prefill?.linkedEntity
			? t(`support.linkedEntity.${prefill.linkedEntity.type}`, { id: prefill.linkedEntity.id })
			: null);

	return (
		<Modal
			icon={<Icon className="text-brand-light" icon={faLifeRing} />}
			isOpened
			cancelButtonText={t("support.modal.cancel")}
			handleClose={onClose}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isCreating || isUploading || Boolean(overrides?.isBusy)}
			size="md"
			submitButtonText={t("support.modal.submit")}
			title={overrides?.title ?? t("support.modal.title")}
		>
			<div className="flex flex-col gap-3">
				<p className="text-sm text-text-meta">{overrides?.intro ?? t("support.modal.intro")}</p>
				{overrides?.leading}

				<div className="flex flex-col gap-1.5">
					<span className="text-xs font-semibold text-text-meta">
						{t("support.modal.categoryLabel")}
					</span>
					<div className="flex flex-wrap gap-2">
						{categories.map((value) => (
							<button
								key={value}
								className={classNames(
									"h-8 cursor-pointer rounded-lg border px-3 text-xs font-semibold transition-colors duration-200",
									value === category
										? "border-transparent bg-blue-action-200 text-brand-light"
										: "border-gray-200 text-text-meta hover:border-brand-light"
								)}
								type="button"
								onClick={() => setCategory(value)}
							>
								{t(`support.category.${value}`)}
							</button>
						))}
					</div>
				</div>

				<Controller
					control={control}
					name="subject"
					render={({ field }) => (
						<InputField
							error={errors.subject ? t("support.modal.errors.subjectRequired") : false}
							label={t("support.modal.subjectLabel")}
							name="subject"
							placeholder={t("support.modal.subjectPlaceholder")}
							showError
							value={field.value ?? ""}
							handleChange={field.onChange}
						/>
					)}
				/>
				{errors.subject && (
					<p className="-mt-2 text-xs text-status-error">
						{t("support.modal.errors.subjectRequired")}
					</p>
				)}

				<Controller
					control={control}
					name="message"
					render={({ field }) => (
						<InputField
							error={errors.message ? t("support.modal.errors.messageRequired") : false}
							label={t("support.modal.messageLabel")}
							multiline
							name="message"
							placeholder={t("support.modal.messagePlaceholder")}
							rows={5}
							showError
							value={field.value ?? ""}
							handleChange={field.onChange}
						/>
					)}
				/>
				{errors.message ? (
					<p className="-mt-2 text-xs text-status-error">
						{t("support.modal.errors.messageRequired")}
					</p>
				) : (
					<span className="-mt-2 text-xs text-text-meta">{t("support.modal.messageHelp")}</span>
				)}

				{linkedEntityText && (
					<div className="flex flex-col gap-1.5">
						<span className="text-xs font-semibold text-text-meta">
							{t("support.modal.relatedLabel")}
						</span>
						<span className="inline-flex h-8 w-fit items-center rounded-lg border border-gray-200 px-3 text-xs font-medium text-text">
							{linkedEntityText}
						</span>
					</div>
				)}

				<div className="flex flex-col gap-1.5">
					<span className="text-xs font-semibold text-text-meta">
						{t("support.modal.attachmentsLabel")} {t("support.modal.optional")}
					</span>
					<label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-gray-200 px-3 py-3 transition-colors duration-200 hover:border-brand-light">
						<Icon className="text-text-meta" icon={faPaperclip} />
						<span className="flex flex-col">
							<span className="text-xs font-semibold text-text">{t("support.modal.dropzone")}</span>
							<span className="text-xs text-text-meta">
								{t("support.modal.dropzoneHint", {
									maxFiles: TICKET_ATTACHMENT_MAX_FILES,
									maxMb: TICKET_ATTACHMENT_MAX_MB,
								})}
							</span>
						</span>
						<input
							accept={TICKET_ATTACHMENT_ALLOWED_MIME_TYPES.join(",")}
							className="hidden"
							multiple
							name="attachments"
							type="file"
							onChange={handlePick}
						/>
					</label>

					{files.length > 0 && (
						<div className="flex flex-wrap gap-2">
							{files.map((file) => (
								<span
									key={file.name}
									className="inline-flex h-8 items-center gap-2 rounded-lg border border-gray-200 px-2.5 text-xs font-medium text-text"
								>
									{file.name}
									<span className="text-text-meta">{formatFileSize(file.size)}</span>
									<button
										aria-label={t("support.modal.remove", { name: file.name })}
										className="cursor-pointer text-text-meta hover:text-text"
										type="button"
										onClick={() => handleRemove(file.name)}
									>
										<Icon icon={faXmark} />
									</button>
								</span>
							))}
						</div>
					)}

					{rejection && (
						<p className="text-xs text-status-error">
							{t(`support.modal.errors.${rejection.key}`, rejection.params)}
						</p>
					)}
				</div>

				{hasError && (
					<p className="text-sm text-status-error">{t("support.modal.errors.createFailed")}</p>
				)}

				<span className="text-xs text-text-meta">
					{overrides?.filedAs ??
						t("support.modal.filedAs", {
							name: [user?.firstName, user?.lastName].filter(Boolean).join(" "),
							company: user?.company?.name ?? "",
						})}
				</span>
			</div>
		</Modal>
	);
};
